import React, { useCallback, useMemo } from 'react';
import { Nullable } from '@tcc/common';
import { PanelCards } from '../../../Panel';
import ApplicationPreview from '../../../../components/cards/applications/ApplicationPreview';
import ApplicationsDetails from '../ApplicationsDetails';
import { Application, ApplicationsData } from '../../../../types/Application';
import { IN_SEASON } from '../../../../constants/mapLayerIds';
import { FHA, PRESCRIPTIONS, WEATHER } from '../../../../constants/cards';
import { useAppSelector } from '../../../../utils';
import { getApplicationTreatmentsBySeason } from '../../../../selectors/applicationTreatmentSelectors';
import { userSelector } from '../../../../selectors/userSelectors';
import { treatmentsToApplicationsData } from '../../../../utils/applicationTreatmentTransformers';

const CARDS = [WEATHER, PRESCRIPTIONS, FHA];
const LAYER = IN_SEASON.APPLICATIONS;

interface ActiveApplication {
  crop: string;
  endDate: number;
}

export interface ApplicationsPanelV2Props {
  activeApplication: ActiveApplication;
  collapsePanel: () => void;
  index: number;
  isCollapsed: boolean;
  isSplitViewEnabled: boolean;
  locale: string;
  season: string;
  setActiveSubLayer: (layer: string, activeSubLayerId: string, index: number, season: string) => void;
  setExpandedApplication: (expandedApplication: Nullable<string>) => void;
  expandedApplication: Nullable<string>;
}

export const ApplicationsPanelV2: React.FC<ApplicationsPanelV2Props> = ({
  activeApplication,
  collapsePanel,
  index,
  isCollapsed,
  isSplitViewEnabled,
  locale,
  season,
  setActiveSubLayer: setActiveSubLayerProp,
  setExpandedApplication,
  expandedApplication
}) => {
  const state = useAppSelector(state => state);
  const user = userSelector(state);
  const fieldUUID = state.field?.uuid?.toUpperCase() ?? '';
  
  const activeSeason = state.seasons.activeSeasons[index];
  const currentSeasonId = activeSeason?.id ?? season;
  
  const treatments = useMemo(
    () => getApplicationTreatmentsBySeason(state, currentSeasonId),
    [state, currentSeasonId]
  );

  const applicationsData: ApplicationsData = useMemo(() => (
    treatmentsToApplicationsData(treatments, {
      userUuid: user?.uuid ?? '',
      fieldUUID,
    })
  ), [treatments, user?.uuid, fieldUUID]);

  const handleApplicationSelect = useCallback((applicationKey: Nullable<string>, {
    name: applicationName,
    date: endDate,
  }: Application) => {
    const endMs = new Date(endDate).getTime();
    const encodedCrop = encodeURIComponent(applicationName);
    const activeSubLayerId = `${endMs}_${encodedCrop}`;

    setActiveSubLayerProp(LAYER, activeSubLayerId, index, season);
    collapsePanel();
    setExpandedApplication(applicationKey)
  }, [setActiveSubLayerProp, index, season, collapsePanel, setExpandedApplication]);

  const getDate = useCallback((date: number) => {
    const dateOpts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: '2-digit',
      year: 'numeric',
      day: '2-digit',
    };
    return new Date(date).toLocaleDateString(locale, dateOpts);
  }, [locale]);

  const renderFirstCard = useCallback(() => {
    if (isSplitViewEnabled && isCollapsed) {
      return (
        <ApplicationPreview
          activeApplicationName={activeApplication ? 'Application' : undefined}
          decrementActiveApplication={() => undefined}
          incrementActiveApplication={() => undefined}
        />
      );
    }

    return (
      <ApplicationsDetails
        applicationsData={applicationsData}
        testId="applications-panel-v2"
        expandedApplication={expandedApplication}
        onToggleApplicationExpand={handleApplicationSelect}
        season={season}
        locale={locale}
      />
    );
  }, [isSplitViewEnabled, isCollapsed, applicationsData, expandedApplication, handleApplicationSelect, season, locale, activeApplication]);

  const activeApplicationDate = activeApplication ? getDate(activeApplication.endDate) : undefined;

  return (
    <PanelCards
      cards={CARDS}
      firstCard={renderFirstCard()}
      headerDetail={activeApplicationDate}
      index={index}
      isCollapsed={isCollapsed}
      testId="application-v2"
    />
  );
};

export default ApplicationsPanelV2;
